import {
    type ApiEndpoint,
    type DownloadUrlResponse,
    isDownloadUrlResponse,
} from "@/shared/api-endpoint";
import type { ParameterRange } from "@/shared/parameter-range";
import { isObject } from "@/shared/utils/typeGuards";

export const kInpaintingMagicFit40Route = "/inpainting-magic-fit-4.0";

// Defaults mirror `InpaintingMagicFitInput` in audio-science-genai
// (`serving/apps/inpainting_magic_fit_4_0/app.py`) so the UI opens on the same
// values the model falls back to when a field is omitted. Complexity is shared
// with Variations 4.0 / Inpainting 4.0 via `kComplexity30Range`, which the 4.0
// models carry over unchanged.
export const kVariationRange: ParameterRange = { min: 0, max: 1, default: 0.13 };
export const kBalanceRange: ParameterRange = { min: 0, max: 1, default: 0.5 };

export interface InpaintingMagicFit40RequestBody {
    drivingAudioKey: string;
    timbreAudioKey: string;
    bpm: number;
    variation?: number;
    balance?: number;
    complexity?: number;
    // Absolute seconds into the model context window. The masked [start, end]
    // range of the timbre audio is regenerated; everything outside it survives
    // into the output. Omitting the pair is the purely generative case.
    inpaintMaskStartSeconds?: number;
    inpaintMaskEndSeconds?: number;
}

export const isInpaintingMagicFit40RequestBody = (
    value: unknown,
): value is InpaintingMagicFit40RequestBody =>
    isObject(value) &&
    typeof value.drivingAudioKey === "string" &&
    typeof value.timbreAudioKey === "string" &&
    typeof value.bpm === "number";

export const kInpaintingMagicFit40Description = "Fill loops to fit another sample";

export const kInpaintingMagicFit40Endpoint: ApiEndpoint<
    InpaintingMagicFit40RequestBody,
    DownloadUrlResponse
> = {
    route: kInpaintingMagicFit40Route,
    label: "Inpainting Magic Fit 4.0",
    source: "Modal",
    isEvaluationOnly: true,
    category: "SpliceGen v4.0",
    description: kInpaintingMagicFit40Description,
    isRequestBody: isInpaintingMagicFit40RequestBody,
    isSuccessResponse: isDownloadUrlResponse,
};
